import { Formik, FormikHelpers, FormikProps, FormikValues } from 'formik';

import { Messages } from '../../codegen/Localize';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { is3rdParty } from '../../settings/siteAuthSettings/services/SiteAuthenticationSettingsService';
import { IServerUserInfo } from '../../ts/models/IServerUserInfo';
import { ISiteUserInfo } from '../../ts/models/ISiteUserInfo';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { UserSettingsNotification,UserSettingsNotificationParams, UserSettingsNotificationType } from '../notifications/UserSettingsNotification';
import { UserSettings } from '../services/resources/UserSettings';
import React = require('react');
import { ButtonWidget,TextLinkKind, TextLinkWidget } from '@tableau/widgets';

import { FormikTextBox } from '../../commonInfra/components/FormikField';
import _ = require('underscore');
import { useEffect } from 'react';

import { LocalAuthConfiguration } from '~src/authentication/models/LocalAuthConfiguration';
import { IPasswordPolicy } from '~src/authentication/models/validation/IPasswordPolicy';
import { ValidationHelpers } from '~src/userManagement/helpers/ValidationHelpers';

import { formError } from '../../settings/styles/CommonStyles';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { textFieldWidget } from '../styles/EmailAndDisplayNameFormStyles';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';

export interface ChangePasswordProps {
    serverUser: IServerUserInfo;
    changePasswordUrl: string;
    isDomainFamilyExternal: boolean;
    isDomainFamilyLocal: boolean;
    siteUser: ISiteUserInfo;
    isUserSelf: boolean;
}
export interface FormFields {
    currentPassword: string;
    confirmPassword: string;
    newPassword: string;
}

export function getIsSAMLUser(siteUser: ISiteUserInfo): boolean {
    return !!siteUser && !!siteUser.authenticationProviderId && is3rdParty(siteUser.authenticationProviderId);
}


export function ChangePassword(changePasswordProps: ChangePasswordProps): JSX.Element {

    const [isEditing, setIsEditing] = React.useState(false);

    const [localAuthConfiguration, setLocalAuthConfiguration] = React.useState(LocalAuthConfiguration.default());

    useEffect(() => {
        LocalAuthConfiguration.fetchCurrentLocalAuthConfiguration()
            .then(serverConfig => setLocalAuthConfiguration(serverConfig));
    }, []);

    const initValues: FormFields = { currentPassword: '', confirmPassword: '', newPassword: '' };

    function handleSubmit(values: FormFields, serverUser: IServerUserInfo, formik: FormikHelpers<FormFields>) {
        try {
            const currentPassword = values.currentPassword;
            const newPassword = values.newPassword;
            UserSettings.changeUserPassword(serverUser.id, newPassword, currentPassword).then((result: IResult) => {
                const userSettingsNotificationParams: UserSettingsNotificationParams = {
                    result: result,
                    username: serverUser.username,
                    userSettingsNotificationType: UserSettingsNotificationType.ChangePassword
                };
                UserSettingsNotification.notify(userSettingsNotificationParams);
                setIsEditing(false);
            });
        } catch (e) {
            setIsEditing(false);
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.errorUnknown() });
        } finally {
            formik.resetForm();
        }
    }


    function validatePassword(values: FormFields) {
        let confirmPassword = values.confirmPassword;
        let newPassword = values.newPassword;
        let currentPassword = values.currentPassword;
        let errors = ({ currentPassword: '', confirmPassword: '', newPassword: '' });
        if (confirmPassword.length > 0 && !(_.isEqual(confirmPassword, newPassword))) {
            errors.confirmPassword = Messages.passwordsDoNotMatch();
        }
        if (newPassword.length > 0 && (_.isEqual(currentPassword, newPassword))) {
            errors.currentPassword = Messages.newPasswordIsSameAsOldPasswordError();
        }
        return (errors.confirmPassword.length > 0 || errors.currentPassword.length > 0) ? errors : null;
    }

    function getFailedPasswordPolicies(newPassword: string): IPasswordPolicy[] {
        return localAuthConfiguration.performPasswordValidation(newPassword);
    }

    function renderButton() {
        return (
            <tr>
                <td className={UserSettingsStyle.labelText}></td>
                <td className="tb-margin-top-wide tb-margin-left-wide">
                    <ButtonWidget
                        testId={'userSetting-changePassword-local'}
                        onClick={() => setIsEditing(true)}
                    >
                        {Messages.changePassword()}
                    </ButtonWidget>
                </td>
            </tr>
        );
    }

    function disableButton(formik: FormikProps<FormikValues>, isUserSelf: boolean) {
        let currentPasswordFieldPresentAndFilled = formik.values.currentPassword && formik.values.currentPassword.length > 0;
        let allFieldsFilled = (isUserSelf ? currentPasswordFieldPresentAndFilled : true) && formik.values.newPassword.length > 0 && formik.values.confirmPassword.length > 0;

        return !allFieldsFilled || formik.isSubmitting || !formik.dirty || !formik.isValid || getFailedPasswordPolicies(formik.values.newPassword).length > 0;
    }

    function renderValidationError(formik: FormikProps<FormikValues>) {
        return (
            <>
                {formik.errors?.confirmPassword &&
                    <div className={formError} data-tb-test-id={'passwords-do-not-match-message'}>
                        {formik.errors.confirmPassword}
                    </div>
                }
                {FeatureFlags.localAuthPasswordExpirationEnabled && formik.errors?.currentPassword &&
                    <div className={formError} data-tb-test-id={'new-password-same-as-old-message'}>
                        {formik.errors.currentPassword}
                    </div>
                }
                {
                    formik.touched.newPassword && ValidationHelpers.createPasswordPolicyErrorMessageSectionFor(getFailedPasswordPolicies(formik.values.newPassword))
                }
            </>
        );
    }

    function renderLocalForm() {
        const serverUser = changePasswordProps.serverUser;
        return (
            <Formik
                initialValues={initValues}
                onSubmit={(values: FormFields, formik: FormikHelpers<FormFields>) => {
                    handleSubmit(values, serverUser, formik);
                }}
                validate={(values: FormFields) => validatePassword(values)}
            >
                {(formik: FormikProps<FormFields>) => (
                    <form onSubmit={formik.handleSubmit}>
                        {!isEditing && renderButton()}
                        {isEditing &&
                            <table>
                                <tbody className={UserSettingsStyle.tbody}>
                                    {changePasswordProps.isUserSelf &&
                                        <tr data-test-id="edit-current-password">
                                            <td className={UserSettingsStyle.labelText}>
                                                {Messages.currentPassword()}
                                            </td>
                                            <td className="tb-details-value">
                                                <FormikTextBox
                                                    className={textFieldWidget}
                                                    type={'password'}
                                                    name="currentPassword"
                                                    label=""
                                                    testId="edit-current-password"
                                                    maxLength={100}
                                                    showValidationMessage={false}
                                                >
                                                </FormikTextBox>
                                            </td>
                                            <td></td>
                                        </tr>}
                                    <tr data-test-id="edit-new-password">
                                        <td className={UserSettingsStyle.labelText}>
                                            {Messages.newPassword()}
                                        </td>
                                        <td className="tb-details-value">
                                            <FormikTextBox
                                                className={textFieldWidget}
                                                type={'password'}
                                                name="newPassword"
                                                label=""
                                                testId="edit-new-password"
                                                maxLength={100}
                                                required={true}
                                                showValidationMessage={false}
                                            >
                                            </FormikTextBox>
                                        </td>
                                    </tr>
                                    <tr data-test-id="edit-confirm-password">
                                        <td className={UserSettingsStyle.labelText}>
                                            {Messages.confirmPassword()}
                                        </td>
                                        <td className="tb-details-value">
                                            <FormikTextBox
                                                className={textFieldWidget}
                                                type={'password'}
                                                name="confirmPassword"
                                                label=""
                                                testId="edit-confirm-password"
                                                disabled={formik.isSubmitting}
                                                maxLength={100}
                                                required={true}
                                                showValidationMessage={false}
                                            >
                                            </FormikTextBox>
                                            {formik.dirty &&
                                                renderValidationError(formik)
                                            }
                                        </td>
                                    </tr>
                                    <InlineCancelAndSaveButtons
                                        testId={'change-password'}
                                        formik={formik}
                                        disabled={disableButton(formik, changePasswordProps.isUserSelf)}
                                        handleClick={() => {
                                            setIsEditing(false);
                                            formik.resetForm();
                                        }}
                                    />
                                </tbody>
                            </table>
                        }
                    </form>
                )}
            </Formik>
        );
    }


    return (
        <table>
            <tbody className={UserSettingsStyle.tbody}>
                {changePasswordProps.isDomainFamilyLocal &&
                    <tr id="settings-password-local">
                        <td className="tb-details-value">
                            {renderLocalForm()}
                        </td>
                        <td></td>
                    </tr>
                }
                {changePasswordProps.isDomainFamilyExternal && !!changePasswordProps.changePasswordUrl
                    && !getIsSAMLUser(changePasswordProps.siteUser) && changePasswordProps.isUserSelf &&
                    <tr id="settings-password-external">
                        <td className={UserSettingsStyle.labelText}></td>
                        <td className="tb-details-value">
                            <div className="tb-margin-top-wide tb-margin-left-wide">
                                <TextLinkWidget
                                    testId={'userSetting-changePassword-link-external'}
                                    kind={TextLinkKind.Standard}
                                    href={changePasswordProps.changePasswordUrl}
                                    target={'_blank'}
                                >
                                    {Messages.changePassword()}
                                </TextLinkWidget>
                            </div>
                        </td>
                    </tr>
                }
            </tbody>
        </table>);

}
